import { useState } from 'react'
import type { CSSProperties } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type {
  Nis2DerivedDeadline, Nis2Obligation, Nis2ObligationInput, Nis2ObligationStatus, Nis2Recurrence,
} from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, StatCard, formatDateTime, formatDay, internalPath } from '../common'
import { DataTable, type Column } from '../DataTable'

// ------------------------------------------------------------------ labels

const RICORRENZE: Nis2Recurrence[] = ['none', 'monthly', 'quarterly', 'semiannual', 'annual']

/** Months between two occurrences (the server's own step, for the preview). */
const MESI_RICORRENZA: Record<Nis2Recurrence, number> = {
  none: 0, monthly: 1, quarterly: 3, semiannual: 6, annual: 12,
}

function etichettaRicorrenza(r: string): string {
  switch (r) {
    case 'none': return t('una tantum')
    case 'monthly': return t('mensile')
    case 'quarterly': return t('trimestrale')
    case 'semiannual': return t('semestrale')
    case 'annual': return t('annuale')
    default: return r
  }
}

function etichettaStato(s: string): string {
  switch (s) {
    case 'da_fare': return t('da fare')
    case 'in_corso': return t('in corso')
    case 'fatto': return t('fatto')
    case 'non_applicabile': return t('non applicabile')
    default: return s
  }
}

function etichettaTiming(timing: string): string {
  switch (timing) {
    case 'scaduto': return t('scaduto')
    case 'in_scadenza': return t('in scadenza')
    case 'da_pianificare': return t('da pianificare')
    case 'pianificato': return t('pianificato')
    case 'fatto': return t('fatto')
    case 'non_applicabile': return t('non applicabile')
    default: return timing
  }
}

const CLASSE_TIMING: Record<string, string> = {
  scaduto: 'badge badge--error',
  in_scadenza: 'badge badge--warn',
  da_pianificare: 'badge badge--info',
  pianificato: 'badge',
  fatto: 'badge badge--ok',
  non_applicabile: 'badge badge--muted',
}

function etichettaFonte(source: string): string {
  switch (source) {
    case 'certificato': return t('certificato')
    case 'contratto': return t('contratto')
    case 'dichiarazione': return t('dichiarazione')
    case 'incidente': return t('incidente')
    default: return source
  }
}

const aperto = (o: Nis2Obligation) => o.status !== 'fatto' && o.status !== 'non_applicabile'

function giorniMancanti(giorni: number | null): string {
  if (giorni === null) return '—'
  if (giorni === 0) return t('oggi')
  if (giorni === 1) return t('domani')
  if (giorni === -1) return t('scaduto ieri')
  return giorni > 0 ? t('fra {n} giorni', { n: giorni }) : t('scaduto da {n} giorni', { n: -giorni })
}

function oreMancanti(ore: number): string {
  if (ore >= 0 && ore < 1) return t('entro un’ora')
  const n = Math.round(Math.abs(ore))
  return ore >= 0 ? t('fra {n} ore', { n }) : t('scaduto da {n} ore', { n })
}

function coloreGiorni(giorni: number | null): CSSProperties | undefined {
  if (giorni === null) return undefined
  if (giorni < 0) return { color: 'var(--error)' }
  if (giorni <= 30) return { color: 'var(--warn)' }
  return undefined
}

// ------------------------------------------------------------------- dates

const pad2 = (n: number) => String(n).padStart(2, '0')

/** Today as a local ISO date (the user's day, not UTC's). */
function oggiIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

/** Adds months to an ISO date, clamping the day like the server does (31/1 + 1 month = 28/2). */
function aggiungiMesi(iso: string, mesi: number): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  const totale = m - 1 + mesi
  const anno = y + Math.floor(totale / 12)
  const mese = totale % 12
  const ultimo = new Date(Date.UTC(anno, mese + 1, 0)).getUTCDate()
  return `${anno}-${pad2(mese + 1)}-${pad2(Math.min(d, ultimo))}`
}

function giornoDopo(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10)
}

/** «30, 7, 1» → [30, 7, 1]; null when something is not a whole number in 0..365. */
function leggiGiorni(testo: string): number[] | null {
  const out: number[] = []
  for (const parte of testo.split(/[,;\s]+/).filter(Boolean)) {
    if (!/^\d+$/.test(parte)) return null
    const n = Number(parte)
    if (n > 365) return null
    out.push(n)
  }
  return [...new Set(out)].sort((a, b) => b - a)
}

// ---------------------------------------------------------------- calendar

/** RFC 5545 text value: backslash, semicolon, comma and newlines escaped. */
function testoIcs(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n')
}

/** RFC 5545 §3.1: content lines longer than 75 octets are folded (CRLF + space). */
function piegaIcs(riga: string): string {
  const enc = new TextEncoder()
  const pezzi: string[] = []
  let corrente = ''
  let ottetti = 0
  for (const ch of riga) {
    const n = enc.encode(ch).length
    // The continuation lines start with a space, which counts.
    const limite = pezzi.length === 0 ? 75 : 74
    if (ottetti + n > limite) {
      pezzi.push(corrente)
      corrente = ''
      ottetti = 0
    }
    corrente += ch
    ottetti += n
  }
  pezzi.push(corrente)
  return pezzi.join('\r\n ')
}

const istanteIcs = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')

/**
 * The open obligations with a due date as an iCalendar file: one all-day
 * event per obligation, with a reminder a week before. CIOs keep their
 * deadlines in their calendar, not in one more console.
 */
function calendarioIcs(obblighi: Nis2Obligation[]): string {
  const adesso = istanteIcs(new Date())
  let pagina = ''
  try {
    pagina = `${window.location.origin}/nis2`
  } catch {
    pagina = ''
  }
  const righe = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//CENSO//NIS2//IT',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${testoIcs(t('Scadenze NIS2'))}`,
  ]
  for (const o of obblighi) {
    if (!o.due_date) continue
    const titolo = `NIS2 · ${o.title}`
    const descrizione = [
      o.reference,
      o.owner || o.owner_email
        ? t('Responsabile: {nome}', { nome: [o.owner, o.owner_email ? `<${o.owner_email}>` : ''].filter(Boolean).join(' ') })
        : '',
      o.window_start ? t('dal {inizio} al {fine}', { inizio: formatDay(o.window_start), fine: formatDay(o.due_date) }) : '',
    ].filter(Boolean).join('\n')
    righe.push(
      'BEGIN:VEVENT',
      `UID:${o.id}@censo-nis2`,
      `DTSTAMP:${adesso}`,
      ...(o.updated_at && !Number.isNaN(Date.parse(o.updated_at)) ? [`LAST-MODIFIED:${istanteIcs(new Date(o.updated_at))}`] : []),
      `DTSTART;VALUE=DATE:${o.due_date.replace(/-/g, '')}`,
      `DTEND;VALUE=DATE:${giornoDopo(o.due_date).replace(/-/g, '')}`,
      `SUMMARY:${testoIcs(titolo)}`,
      ...(descrizione ? [`DESCRIPTION:${testoIcs(descrizione)}`] : []),
      ...(pagina ? [`URL:${pagina}`] : []),
      'TRANSP:TRANSPARENT',
      'BEGIN:VALARM',
      'ACTION:DISPLAY',
      `DESCRIPTION:${testoIcs(titolo)}`,
      'TRIGGER:-P7D',
      'END:VALARM',
      'END:VEVENT',
    )
  }
  righe.push('END:VCALENDAR')
  return righe.map(piegaIcs).join('\r\n') + '\r\n'
}

function scaricaIcs(obblighi: Nis2Obligation[]) {
  const blob = new Blob([calendarioIcs(obblighi)], { type: 'text/calendar;charset=utf-8' })
  const indirizzo = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = indirizzo
  a.download = `censo-nis2-scadenze-${oggiIso()}.ics`
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(indirizzo), 1000)
}

// ------------------------------------------------------------------- cells

const TRONCATO: CSSProperties = {
  display: 'inline-block', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis',
  whiteSpace: 'nowrap', verticalAlign: 'bottom',
}

/** The evidence, cut to one line; a plain http(s) address opens in a new tab. */
function CellaEvidenza({ testo }: { testo: string }) {
  const v = testo.trim()
  if (!v) return <span className="muted">—</span>
  if (/^https?:\/\/\S+$/i.test(v)) {
    return <a href={v} target="_blank" rel="noopener noreferrer" style={TRONCATO} title={v}>{v}</a>
  }
  return <span style={TRONCATO} title={v}>{v}</span>
}

// -------------------------------------------------------------------- form

/** Create or edit an obligation; the status «fatto» goes through «Fatto», not here. */
function ModuloAdempimento({ iniziale, onSalvato, onAnnulla }: {
  iniziale: Nis2Obligation | null
  onSalvato: (o: Nis2Obligation) => Promise<void>
  onAnnulla: () => void
}) {
  const [titolo, setTitolo] = useState(iniziale?.title ?? '')
  const [riferimento, setRiferimento] = useState(iniziale?.reference ?? '')
  const [descrizione, setDescrizione] = useState(iniziale?.description ?? '')
  const [responsabile, setResponsabile] = useState(iniziale?.owner ?? '')
  const [email, setEmail] = useState(iniziale?.owner_email ?? '')
  const [inizio, setInizio] = useState(iniziale?.window_start ?? '')
  const [scadenza, setScadenza] = useState(iniziale?.due_date ?? '')
  const [ricorrenza, setRicorrenza] = useState<Nis2Recurrence>(iniziale?.recurrence ?? 'none')
  const [stato, setStato] = useState<Nis2ObligationStatus>(iniziale?.status ?? 'da_fare')
  const [promemoria, setPromemoria] = useState((iniziale?.reminder_days ?? []).join(', '))
  const [note, setNote] = useState(iniziale?.notes ?? '')
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)

  // «fatto» is offered only to a row already done (to keep it, or reopen it).
  const stati: Nis2ObligationStatus[] = iniziale?.status === 'fatto'
    ? ['da_fare', 'in_corso', 'fatto', 'non_applicabile']
    : ['da_fare', 'in_corso', 'non_applicabile']

  async function salva() {
    const giorni = leggiGiorni(promemoria)
    if (!titolo.trim()) {
      setErrore(new Error(t('Serve un titolo.')))
      return
    }
    if (giorni === null) {
      setErrore(new Error(t('I giorni di promemoria sono numeri interi fra 0 e 365, separati da virgola.')))
      return
    }
    if (email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setErrore(new Error(t('L’indirizzo e-mail del responsabile non è valido.')))
      return
    }
    if (inizio && scadenza && inizio > scadenza) {
      setErrore(new Error(t('L’inizio della finestra non può essere dopo la scadenza.')))
      return
    }
    const corpo: Nis2ObligationInput = {
      title: titolo.trim(),
      reference: riferimento.trim(),
      description: descrizione.trim(),
      owner: responsabile.trim(),
      owner_email: email.trim(),
      window_start: inizio || null,
      due_date: scadenza || null,
      recurrence: ricorrenza,
      status: stato,
      reminder_days: giorni,
      notes: note.trim(),
    }
    setBusy(true)
    setErrore(null)
    try {
      const o = iniziale
        ? await api.updateNis2Obligation(iniziale.id, corpo)
        : await api.createNis2Obligation(corpo)
      await onSalvato(o)
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ padding: '8px 4px' }}>
      <h3 style={{ marginTop: 0 }}>{iniziale ? t('Modifica l’adempimento') : t('Nuovo adempimento')}</h3>
      {iniziale?.kind === 'normativo' && (
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Adempimento previsto dalla norma: le date vengono dalle determinazioni ACN in vigore quando è stato caricato. Se una determinazione le sposta, correggile qui.')}
        </p>
      )}
      <ErrorBanner error={errore} />
      <div className="eagle-report__riga">
        <label className="field" style={{ flex: 2 }}>
          <span>{t('Titolo')}</span>
          <input value={titolo} onChange={(e) => setTitolo(e.target.value)} maxLength={300} />
        </label>
        <label className="field">
          <span>{t('Riferimento normativo')}</span>
          <input value={riferimento} onChange={(e) => setRiferimento(e.target.value)} maxLength={200}
                 className="mono" placeholder={t('D.Lgs. 138/2024, art. 24')} />
        </label>
      </div>
      <label className="field">
        <span>{t('Descrizione')}</span>
        <textarea rows={3} value={descrizione} onChange={(e) => setDescrizione(e.target.value)} />
      </label>
      <div className="eagle-report__riga">
        <label className="field">
          <span>{t('Responsabile')}</span>
          <input value={responsabile} onChange={(e) => setResponsabile(e.target.value)} maxLength={200} />
        </label>
        <label className="field">
          <span>{t('E-mail del responsabile')}</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={320}
                 placeholder={t('nome@azienda.it')} />
          <span className="field__hint">{t('Riceve i promemoria della scadenza.')}</span>
        </label>
      </div>
      <div className="eagle-report__riga">
        <label className="field" style={{ maxWidth: 200 }}>
          <span>{t('Inizio della finestra')}</span>
          <input type="date" value={inizio} onChange={(e) => setInizio(e.target.value)} />
        </label>
        <label className="field" style={{ maxWidth: 200 }}>
          <span>{t('Scadenza')}</span>
          <input type="date" value={scadenza} onChange={(e) => setScadenza(e.target.value)} />
        </label>
        <label className="field" style={{ maxWidth: 200 }}>
          <span>{t('Ricorrenza')}</span>
          <select value={ricorrenza} onChange={(e) => setRicorrenza(e.target.value as Nis2Recurrence)}>
            {RICORRENZE.map((r) => <option key={r} value={r}>{etichettaRicorrenza(r)}</option>)}
          </select>
        </label>
        <label className="field" style={{ maxWidth: 200 }}>
          <span>{t('Stato')}</span>
          <select value={stato} onChange={(e) => setStato(e.target.value as Nis2ObligationStatus)}>
            {stati.map((s) => <option key={s} value={s}>{etichettaStato(s)}</option>)}
          </select>
        </label>
      </div>
      <p className="field__hint" style={{ marginTop: -8 }}>
        {t('La finestra serve agli adempimenti con un periodo, come la registrazione dal 1° gennaio al 28 febbraio. Per chiudere un adempimento usa «Fatto»: registra l’evidenza e, se è ricorrente, prepara la prossima occorrenza.')}
      </p>
      <div className="eagle-report__riga">
        <label className="field" style={{ maxWidth: 260 }}>
          <span>{t('Promemoria (giorni prima)')}</span>
          <input value={promemoria} onChange={(e) => setPromemoria(e.target.value)} placeholder="30, 7, 1" />
          <span className="field__hint">{t('Separati da virgola. Vuoto = 30, 7 e 1 giorno prima.')}</span>
        </label>
        <label className="field" style={{ flex: 2 }}>
          <span>{t('Note')}</span>
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
      </div>
      <div className="eagle-form__azioni">
        <button type="button" disabled={busy} onClick={() => void salva()}>
          {busy ? t('Salvo…') : t('Salva')}
        </button>
        <button type="button" className="secondary" disabled={busy} onClick={onAnnulla}>{t('Annulla')}</button>
      </div>
    </div>
  )
}

/** «Fatto»: the evidence, then the server closes it and plans the next occurrence. */
function Completa({ o, onFatto, onAnnulla }: {
  o: Nis2Obligation
  onFatto: (fatto: Nis2Obligation, prossimo: Nis2Obligation | null) => Promise<void>
  onAnnulla: () => void
}) {
  const [evidenza, setEvidenza] = useState(o.evidence)
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const mesi = MESI_RICORRENZA[o.recurrence] ?? 0
  const prossima = mesi ? aggiungiMesi(o.due_date ?? oggiIso(), mesi) : null

  async function conferma() {
    if (!evidenza.trim() && !window.confirm(t('Nessuna evidenza: segnarlo fatto comunque? In un’ispezione l’evidenza è ciò che dimostra l’adempimento.'))) return
    setBusy(true)
    setErrore(null)
    try {
      const r = await api.completeNis2Obligation(o.id, evidenza.trim())
      await onFatto(r.done, r.next)
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ padding: '8px 4px' }}>
      <h3 style={{ marginTop: 0 }}>{t('Segna come fatto: «{titolo}»', { titolo: o.title })}</h3>
      <ErrorBanner error={errore} />
      <label className="field">
        <span>{t('Evidenza')}</span>
        <textarea rows={3} value={evidenza} onChange={(e) => setEvidenza(e.target.value)} autoFocus
                  placeholder={t('numero di protocollo, documento, collegamento…')} />
        <span className="field__hint">
          {t('Ciò che dimostra l’adempimento in un’ispezione: il protocollo della comunicazione ad ACN, il verbale, il documento o il collegamento dove si trova.')}
        </span>
      </label>
      {prossima && (
        <p className="field__hint">
          {t('È {ricorrenza}: alla conferma nasce la prossima occorrenza, in scadenza il {data}. Questa resta nello storico con la sua evidenza.', {
            ricorrenza: etichettaRicorrenza(o.recurrence), data: formatDay(prossima),
          })}
        </p>
      )}
      <div className="eagle-form__azioni">
        <button type="button" disabled={busy} onClick={() => void conferma()}>
          {busy ? t('Salvo…') : t('Segna come fatto')}
        </button>
        <button type="button" className="secondary" disabled={busy} onClick={onAnnulla}>{t('Annulla')}</button>
      </div>
    </div>
  )
}

// -------------------------------------------------------------------- page

type Pannello = { id: string; modo: 'fatto' | 'modifica' }

/**
 * The NIS2 deadlines a CISO and a CIO work from: the obligations of D.Lgs.
 * 138/2024 (seeded, editable) and the organisation's own, closed with their
 * evidence, plus the deadlines the platform already knows (certificates,
 * ICT contracts, declarations, incident clocks), handled where they live.
 */
export function ScadenzeNis2({ scrive }: { scrive: boolean }) {
  const dati = useAsync(() => api.nis2Deadlines(), [])
  const [mostraFatti, setMostraFatti] = useState(false)
  const [pannello, setPannello] = useState<Pannello | null>(null)
  const [nuovo, setNuovo] = useState(false)
  const [esito, setEsito] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  if (dati.loading && !dati.data) return <Spinner />
  if (!dati.data) return <ErrorBanner error={dati.error ?? new Error(t('Scadenze NIS2 non disponibili.'))} />

  const { obligations, derived, summary, templates_missing: mancanti } = dati.data
  const righe = mostraFatti ? obligations : obligations.filter(aperto)
  const chiusi = obligations.length - obligations.filter(aperto).length
  const daCalendario = obligations.filter((o) => aperto(o) && o.due_date)

  async function carica() {
    const prima = dati.data?.obligations.length ?? 0
    setBusy(true)
    setErrore(null)
    setEsito(null)
    try {
      const r = await api.nis2SeedObligations()
      await dati.reload()
      const n = r.obligations.length - prima
      setEsito(n > 0
        ? t('Caricati {n} adempimenti NIS2: controlla date e responsabili.', { n })
        : t('Nessun adempimento nuovo da caricare; le date mancanti sono state riprese dal profilo NIS2, dove possibile.'))
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  async function elimina(o: Nis2Obligation) {
    if (!window.confirm(t('Eliminare l’adempimento «{titolo}»? Per uno che non vi riguarda è meglio lo stato «non applicabile», che resta come scelta motivata.', { titolo: o.title }))) return
    setBusy(true)
    setErrore(null)
    setEsito(null)
    try {
      await api.deleteNis2Obligation(o.id)
      if (pannello?.id === o.id) setPannello(null)
      await dati.reload()
      setEsito(t('Adempimento eliminato.'))
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  async function salvato() {
    setPannello(null)
    setNuovo(false)
    await dati.reload()
    setEsito(t('Adempimento salvato.'))
  }

  async function fatto(o: Nis2Obligation, prossimo: Nis2Obligation | null) {
    setPannello(null)
    await dati.reload()
    setEsito(prossimo
      ? t('«{titolo}» segnato come fatto. Creata la prossima occorrenza, in scadenza il {data}.', {
          titolo: o.title, data: formatDay(prossimo.due_date),
        })
      : t('«{titolo}» segnato come fatto.', { titolo: o.title }))
  }

  const apri = (o: Nis2Obligation, modo: Pannello['modo']) => {
    setNuovo(false)
    setEsito(null)
    setPannello(pannello?.id === o.id && pannello.modo === modo ? null : { id: o.id, modo })
  }

  const colonne: Column<Nis2Obligation>[] = [
    {
      key: 'timing',
      header: t('Situazione'),
      value: (o) => etichettaTiming(o.timing),
      render: (o) => <span className={CLASSE_TIMING[o.timing] ?? 'badge'}>{etichettaTiming(o.timing)}</span>,
      // The server order (overdue first, then by date) is the meaningful one;
      // alphabetical on these labels would not be.
      sort: false,
      nowrap: true,
    },
    {
      key: 'title',
      header: t('Adempimento'),
      value: (o) => `${o.title} ${o.reference}`,
      filter: 'text',
      render: (o) => (
        <div title={o.description || undefined}>
          {o.title}
          {o.kind === 'normativo' && <span className="chip" style={{ marginLeft: 6 }}>{t('normativo')}</span>}
          {o.reference && <div className="mono muted" style={{ fontSize: 12 }}>{o.reference}</div>}
        </div>
      ),
    },
    {
      key: 'kind',
      header: t('Tipo'),
      value: (o) => (o.kind === 'normativo' ? t('normativo') : t('interno')),
      hidden: true,
    },
    {
      key: 'owner',
      header: t('Responsabile'),
      value: (o) => o.owner || o.owner_email,
      render: (o) => (o.owner || o.owner_email
        ? (
          <>
            {o.owner || o.owner_email}
            {o.owner && o.owner_email && <div className="muted" style={{ fontSize: 12 }}>{o.owner_email}</div>}
          </>
        )
        : <span className="muted">{t('da assegnare')}</span>),
    },
    {
      key: 'due_date',
      header: t('Scadenza'),
      type: 'date',
      value: (o) => o.due_date ?? '',
      render: (o) => (o.due_date
        ? (o.window_start
          ? t('dal {inizio} al {fine}', { inizio: formatDay(o.window_start), fine: formatDay(o.due_date) })
          : formatDay(o.due_date))
        : <span className="muted">—</span>),
    },
    {
      key: 'days_left',
      header: t('Mancano'),
      type: 'number',
      value: (o) => (aperto(o) ? o.days_left : null),
      render: (o) => (aperto(o) && o.days_left !== null
        ? <span style={coloreGiorni(o.days_left)}>{giorniMancanti(o.days_left)}</span>
        : <span className="muted">—</span>),
    },
    {
      key: 'recurrence',
      header: t('Ricorrenza'),
      value: (o) => etichettaRicorrenza(o.recurrence),
    },
    {
      key: 'status',
      header: t('Stato'),
      value: (o) => etichettaStato(o.status),
      render: (o) => (
        <>
          {etichettaStato(o.status)}
          {o.status === 'fatto' && o.completed_at && (
            <div className="muted" style={{ fontSize: 12 }}>
              {t('il {data}', { data: formatDay(o.completed_at) })}{o.completed_by ? ` · ${o.completed_by}` : ''}
            </div>
          )}
        </>
      ),
    },
    {
      key: 'evidence',
      header: t('Evidenza'),
      value: (o) => o.evidence,
      filter: 'text',
      render: (o) => <CellaEvidenza testo={o.evidence} />,
    },
    {
      key: 'notes',
      header: t('Note'),
      value: (o) => o.notes,
      filter: 'text',
      hidden: true,
    },
  ]
  if (scrive) {
    colonne.push({
      key: 'azioni',
      header: '',
      sort: false,
      filter: false,
      searchable: false,
      nowrap: true,
      render: (o) => (
        <span style={{ display: 'inline-flex', gap: 10 }}>
          {aperto(o) && (
            <button type="button" className="link" disabled={busy} onClick={() => apri(o, 'fatto')}>{t('Fatto')}</button>
          )}
          <button type="button" className="link" disabled={busy} onClick={() => apri(o, 'modifica')}>{t('Modifica')}</button>
          <button type="button" className="link" disabled={busy} onClick={() => void elimina(o)}>{t('Elimina')}</button>
        </span>
      ),
    })
  }

  const colonneRicavate: Column<Nis2DerivedDeadline>[] = [
    {
      key: 'source',
      header: t('Fonte'),
      value: (d) => etichettaFonte(d.source),
      render: (d) => (
        <span
          className="chip"
          style={d.source === 'incidente'
            ? { color: 'var(--error)', borderColor: 'var(--error)', background: 'var(--error-bg)' }
            : undefined}
        >
          {etichettaFonte(d.source)}
        </span>
      ),
    },
    {
      key: 'title',
      header: t('Cosa'),
      value: (d) => d.title,
      filter: 'text',
      render: (d) => <Link to={internalPath(d.link)}>{d.title}</Link>,
    },
    {
      key: 'due_date',
      header: t('Entro'),
      type: 'date',
      value: (d) => d.due_at ?? d.due_date,
      render: (d) => (d.source === 'incidente' && d.due_at ? formatDateTime(d.due_at) : formatDay(d.due_date)),
    },
    {
      key: 'days_left',
      header: t('Mancano'),
      type: 'number',
      // Incidents count in hours: a fraction of a day keeps them in order.
      value: (d) => (d.hours_left != null ? Math.round((d.hours_left / 24) * 10) / 10 : d.days_left),
      render: (d) => (d.source === 'incidente' && d.hours_left != null && d.hours_left < 72
        ? <strong style={{ color: 'var(--error)' }}>{oreMancanti(d.hours_left)}</strong>
        : <span style={coloreGiorni(d.days_left)}>{giorniMancanti(d.days_left)}</span>),
    },
    {
      key: 'reference',
      header: t('Riferimento'),
      value: (d) => d.reference,
      render: (d) => (d.reference ? <span className="mono muted" style={{ fontSize: 12 }}>{d.reference}</span> : <span className="muted">—</span>),
    },
  ]

  const daCaricare = scrive && (!summary.seeded || mancanti.length > 0)

  return (
    <div>
      <div className="stats">
        <StatCard
          label={t('Scaduti')}
          value={summary.overdue}
          tone={summary.overdue > 0 ? 'error' : 'ok'}
          hint={t('adempimenti e scadenze ricavate')}
        />
        <StatCard
          label={t('In scadenza entro 30 giorni')}
          value={summary.due_30}
          tone={summary.due_30 > 0 ? 'warn' : undefined}
        />
        <StatCard
          label={t('Da pianificare')}
          value={summary.unplanned}
          hint={t('senza una data di scadenza')}
        />
        <StatCard
          label={t('Fatti negli ultimi 12 mesi')}
          value={summary.done_12m}
          tone="ok"
        />
      </div>

      {daCaricare && (
        <div className="banner banner--info">
          <strong>
            {summary.seeded
              ? t('{n} adempimenti previsti dalla norma non sono in elenco', { n: mancanti.length })
              : t('Adempimenti NIS2 da caricare')}
          </strong>
          <p style={{ margin: '6px 0' }}>
            {summary.seeded
              ? t('Eliminati, o arrivati con un aggiornamento di CENSO. Si caricano senza toccare quelli già presenti.')
              : t('Gli obblighi con una scadenza del D.Lgs. 138/2024 e delle determinazioni ACN: registrazione annuale sulla piattaforma ACN, aggiornamento delle informazioni, notifica degli incidenti e misure di base dalla comunicazione di inclusione, approvazione e formazione degli organi di amministrazione, formazione del personale, analisi dei rischi, test di continuità, audit, fornitori. Ognuno con il suo riferimento, tutti modificabili.')}
          </p>
          <p style={{ margin: '6px 0' }}>
            {t('Le date vengono dalla norma e dalle determinazioni ACN, che possono cambiare: verificale con le determinazioni in vigore. Quelli che contano dalla comunicazione di inclusione (9 e 18 mesi) prendono la data dal profilo NIS2; se manca, restano da pianificare.')}
          </p>
          <button type="button" disabled={busy} onClick={() => void carica()}>
            {busy ? t('Carico…') : t('Carica gli adempimenti NIS2')}
          </button>
        </div>
      )}

      <Card
        title={t('Adempimenti')}
        actions={
          <>
            {scrive && (
              <button type="button" disabled={busy || nuovo} onClick={() => { setNuovo(true); setPannello(null); setEsito(null) }}>
                {t('Nuovo adempimento')}
              </button>
            )}
            <button
              type="button"
              className="secondary"
              disabled={daCalendario.length === 0}
              title={t('Gli adempimenti aperti con una data, come eventi di un giorno con un promemoria una settimana prima: si importano in Outlook, Google Calendar o Apple Calendar.')}
              onClick={() => scaricaIcs(daCalendario)}
            >
              {t('Esporta calendario (.ics)')}
            </button>
          </>
        }
      >
        {esito && <div className="banner banner--ok">{esito}</div>}
        <ErrorBanner error={errore} />
        <ErrorBanner error={dati.error} />

        {nuovo && (
          <div style={{ border: '1px solid var(--divider)', borderRadius: 'var(--radius-sm)', padding: 12, marginBottom: 16 }}>
            <ModuloAdempimento iniziale={null} onSalvato={salvato} onAnnulla={() => setNuovo(false)} />
          </div>
        )}

        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Promemoria per e-mail a 30, 7 e 1 giorno (o ai giorni scelti), il giorno della scadenza e una volta a scadenza passata: al responsabile, al punto di contatto NIS2 e ai destinatari degli avvisi del tenant. Si spengono in')}{' '}
          <Link to="/settings">{t('Impostazioni')}</Link>
          {' → '}{t('notifiche, «Avvisa sulle scadenze NIS2 e sugli incidenti».')}
        </p>

        {obligations.length === 0 ? (
          <EmptyState
            title={t('Nessun adempimento in elenco')}
            hint={scrive
              ? t('Carica gli adempimenti NIS2 previsti dalla norma, o aggiungi quelli interni con «Nuovo adempimento».')
              : t('Chi ha i permessi di scrittura può caricare gli adempimenti NIS2 previsti dalla norma.')}
          />
        ) : (
          <>
            <div className="toolbar" style={{ marginBottom: 8 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={mostraFatti} onChange={(e) => setMostraFatti(e.target.checked)} />
                {t('Mostra anche quelli fatti')}
                {chiusi > 0 && <span className="muted">({chiusi})</span>}
              </label>
            </div>
            <DataTable
              id="nis2-adempimenti"
              columns={colonne}
              rows={righe}
              rowKey={(o) => o.id}
              conteggio={{ nome: t('adempimenti') }}
              emptyText={t('Nessun adempimento aperto.')}
              expanded={(o) => pannello?.id === o.id}
              expand={(o) => (pannello?.modo === 'fatto'
                ? <Completa o={o} onFatto={fatto} onAnnulla={() => setPannello(null)} />
                : <ModuloAdempimento iniziale={o} onSalvato={salvato} onAnnulla={() => setPannello(null)} />)}
            />
          </>
        )}
      </Card>

      <Card title={t('Scadenze dai dati della piattaforma')}>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Lette dai dati, non copiate: certificati dei sistemi del perimetro o esposti che scadono entro 60 giorni, contratti ICT che finiscono entro 90, dichiarazioni non riesaminate da un anno, passi di notifica degli incidenti aperti. Si gestiscono dove stanno: il certificato sul CI, il contratto in Contratti, la dichiarazione e l’incidente in questa pagina.')}
        </p>
        {derived.length === 0 ? (
          <p className="muted">{t('Nessuna scadenza ricavata nei prossimi mesi.')}</p>
        ) : (
          <DataTable
            id="nis2-scadenze-ricavate"
            columns={colonneRicavate}
            rows={derived}
            rowKey={(d) => `${d.source}|${d.link}|${d.title}|${d.due_at ?? d.due_date}`}
            conteggio={{ nome: t('scadenze') }}
          />
        )}
      </Card>
    </div>
  )
}
